import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";

import { setXClientHost } from "../../app/utils";
import {
  createContent,
  fetchContentByName,
  selectContents,
  updateContent,
} from "../content/contentsSlice";

const ColorButton = withStyles((theme) => ({
  root: {
    margin: theme.spacing(0, 0, 0),
    backgroundColor: theme.palette.primaryButton.main,
    color: theme.palette.primaryButtonFont.main,
    "&:hover": {
      backgroundColor: theme.palette.primaryButton.main,
      opacity: 0.7,
    },
    width: 200,
  },
}))(Button);

const useStyles = makeStyles((theme) => ({
  customisationContainer: {
    padding: 24,
    borderRadius: 16,
    color: theme.palette.primaryFont.main,
    fontSize: 16,
    width: "75%",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 16,
    color: theme.palette.secondaryFont.main,
  },
  subTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    marginTop: 16,
    color: theme.palette.secondaryFont.main,
  },
  sectionContainer: {
    marginBottom: 32,
  },
  inputLabel: {
    marginBottom: 8,
    display: "block",
    fontWeight: "bold",
  },
  pickerInput: {
    width: "100%",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 24,
  },
}));

const SECTION_COUNT = 6;
const PRODUCTS_PER_SECTION = 4;

export default function CustomPromoPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  let jwtToken = localStorage.getItem("clientToken");

  const { promoPageData } = useSelector(selectContents);

  const [sections, setSections] = useState(
    Array.from({ length: SECTION_COUNT }, () =>
      Array.from({ length: PRODUCTS_PER_SECTION }, () => "")
    )
  );

  useEffect(() => {
    if (!promoPageData.value && !promoPageData.loading && !promoPageData.fetched) {
      dispatch(
        fetchContentByName({
          host: setXClientHost(),
          token: jwtToken,
          name: "promo_page_data",
        })
      );
    }
  }, [dispatch, jwtToken, promoPageData.value, promoPageData.loading, promoPageData.fetched]);

  useEffect(() => {
    if (promoPageData.value) {
      try {
        const allData = JSON.parse(promoPageData.value);
        if (Array.isArray(allData)) {
          const newSections = allData.map(section => {
            const paddedSkus = [...section, "", "", "", ""].slice(0, 4);
            return paddedSkus;
          });
          
          // Pad sections if there are fewer than SECTION_COUNT
          while (newSections.length < SECTION_COUNT) {
            newSections.push(["", "", "", ""]);
          }

          setSections(newSections.slice(0, SECTION_COUNT));
        }
      } catch (e) {
        console.error("Error parsing promoPageData", e);
      }
    }
  }, [promoPageData.value]);

  const handleSkuChange = (sectionIndex, productIndex, value) => {
    const newSections = [...sections];
    newSections[sectionIndex] = [...newSections[sectionIndex]];
    newSections[sectionIndex][productIndex] = value;
    setSections(newSections);
  };

  const handleSave = () => {
    const cleanedData = sections.map(skus => skus.filter(sku => sku.trim() !== ""));
    const stringValue = JSON.stringify(cleanedData);
    
    if (promoPageData.id) {
      dispatch(
        updateContent({
          token: jwtToken,
          id: promoPageData.id,
          content: { value: stringValue },
          name: "promo_page_data",
        })
      );
    } else {
      dispatch(
        createContent({
          token: jwtToken,
          content: {
            name: "promo_page_data",
            value: stringValue,
            classification: "data",
            storageKey: null,
          },
        })
      );
    }
  };

  return (
    <Paper elevation={3} className={classes.customisationContainer}>
      <div className={classes.title}>Promo Page Customisation</div>
      <Typography variant="body1" gutterBottom>
        Enter up to 4 product SKUs for each of the 6 sections.
      </Typography>
      <Divider style={{ marginTop: 16, marginBottom: 24 }} />

      {sections.map((skus, sectionIndex) => (
        <div key={sectionIndex} className={classes.sectionContainer}>
          <div className={classes.subTitle}>Section {sectionIndex + 1}</div>
          <Grid container spacing={2}>
            {skus.map((sku, productIndex) => (
              <Grid item xs={12} sm={6} md={3} key={productIndex}>
                <label className={classes.inputLabel}>
                  Product {productIndex + 1} SKU:
                </label>
                <TextField
                  className={classes.pickerInput}
                  variant="outlined"
                  margin="dense"
                  value={sku}
                  onChange={(e) =>
                    handleSkuChange(sectionIndex, productIndex, e.target.value)
                  }
                />
              </Grid>
            ))}
          </Grid>
          {sectionIndex < SECTION_COUNT - 1 && (
            <Divider style={{ marginTop: 24 }} />
          )}
        </div>
      ))}

      <div className={classes.buttonsContainer}>
        <ColorButton variant="contained" onClick={handleSave}>
          Save Changes
        </ColorButton>
      </div>
    </Paper>
  );
}
